import React from 'react';
import useInView from '../hooks/useInView';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../translations/translations';

const AboutSection = () => {
  const [sectionRef, sectionInView] = useInView();
  const { language } = useLanguage();

  return (
    <section className="py-16 md:py-24 bg-black relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-20 left-20 w-96 h-96 bg-[#DC1A5F] rounded-full filter blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#32D6E0] rounded-full filter blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
      </div>

      <div className="container mx-auto px-6 md:px-8 relative z-10">
        <div 
          ref={sectionRef}
          className="max-w-6xl mx-auto"
        >
          {/* Titre */}
          <h2 
            className={`text-4xl md:text-5xl lg:text-6xl font-bold mb-6 md:mb-8 text-center transition-all duration-1000 ease-out ${
              sectionInView 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 -translate-y-10'
            }`}
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            <span className="gradient-text">{getTranslation(language, 'about.title')}</span>
          </h2>

          {/* Introduction */}
          <p 
            className={`text-xl md:text-2xl lg:text-3xl font-semibold text-white text-center mb-12 md:mb-16 max-w-4xl mx-auto transition-all duration-1000 ease-out delay-200 ${
              sectionInView 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 -translate-y-10'
            }`}
          >
            {getTranslation(language, 'about.intro').split('<name>')[0]}
            <span className="text-white font-bold">Wassim Mhadhbi</span>
            {getTranslation(language, 'about.intro').split('</name>')[1] && (
              <>
                <br className="hidden md:block" />
                <span className="text-gray-300 font-normal">
                  {getTranslation(language, 'about.intro').split('</name>')[1]}
                </span>
              </>
            )}
          </p>

          {/* Grid layout pour meilleure organisation */}
          <div className="grid lg:grid-cols-2 gap-8 md:gap-12 mb-12">
            {/* Conviction - Colonne gauche */}
            <div 
              className={`bg-gradient-to-br from-gray-900 to-gray-800 p-8 md:p-10 rounded-3xl border-2 border-[#DC1A5F]/30 shadow-lg hover:shadow-2xl hover:shadow-[#DC1A5F]/20 transition-all duration-700 ${
                sectionInView 
                  ? 'opacity-100 translate-x-0 scale-100' 
                  : 'opacity-0 -translate-x-20 scale-95'
              }`}
              style={{ transitionDelay: '400ms' }}
            >
              <div className="flex items-start mb-4">
                <div className="w-1 h-16 bg-gradient-to-b from-[#DC1A5F] to-[#32D6E0] rounded-full mr-5"></div>
                <div>
                  <h3 className="text-2xl md:text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {getTranslation(language, 'about.conviction.title')}
                  </h3>
                  <p className="text-lg md:text-xl text-gray-100 leading-relaxed">
                    <strong className="text-[#DC1A5F]">{getTranslation(language, 'about.conviction.text')}</strong>
                  </p>
                </div>
              </div>
              <p className="text-base md:text-lg text-gray-300 leading-relaxed mt-4 pl-6">
                {getTranslation(language, 'about.conviction.description')}
              </p>
            </div>

            {/* Fondamentaux - Colonne droite */}
            <div 
              className={`bg-gradient-to-br from-gray-800 to-gray-900 p-8 md:p-10 rounded-3xl border-2 border-[#32D6E0]/30 shadow-lg hover:shadow-2xl hover:shadow-[#32D6E0]/20 transition-all duration-700 ${
                sectionInView 
                  ? 'opacity-100 translate-x-0 scale-100' 
                  : 'opacity-0 translate-x-20 scale-95'
              }`}
              style={{ transitionDelay: '600ms' }}
            >
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
                {getTranslation(language, 'about.fundamentals.title')}
              </h3>
              <div className="space-y-5">
                <div className="flex items-start space-x-4 group">
                  <div className="w-3 h-3 rounded-full bg-[#DC1A5F] mt-2 flex-shrink-0 group-hover:scale-150 transition-transform duration-300"></div>
                  <p className="text-base md:text-lg text-gray-200" dangerouslySetInnerHTML={{ 
                    __html: getTranslation(language, 'about.fundamentals.item1').replace(/\*\*(.*?)\*\*/g, '<strong class="text-white">$1</strong>') 
                  }} />
                </div>
                <div className="flex items-start space-x-4 group">
                  <div className="w-3 h-3 rounded-full bg-[#32D6E0] mt-2 flex-shrink-0 group-hover:scale-150 transition-transform duration-300"></div>
                  <p className="text-base md:text-lg text-gray-200" dangerouslySetInnerHTML={{ 
                    __html: getTranslation(language, 'about.fundamentals.item2').replace(/\*\*(.*?)\*\*/g, '<strong class="text-white">$1</strong>') 
                  }} />
                </div>
                <div className="flex items-start space-x-4 group">
                  <div className="w-3 h-3 rounded-full bg-[#DC1A5F] mt-2 flex-shrink-0 group-hover:scale-150 transition-transform duration-300"></div>
                  <p className="text-base md:text-lg text-gray-200" dangerouslySetInnerHTML={{ 
                    __html: getTranslation(language, 'about.fundamentals.item3').replace(/\*\*(.*?)\*\*/g, '<strong class="text-white">$1</strong>') 
                  }} />
                </div>
                <div className="flex items-start space-x-4 group">
                  <div className="w-3 h-3 rounded-full bg-[#32D6E0] mt-2 flex-shrink-0 group-hover:scale-150 transition-transform duration-300"></div>
                  <p className="text-base md:text-lg text-gray-200" dangerouslySetInnerHTML={{ 
                    __html: getTranslation(language, 'about.fundamentals.item4').replace(/\*\*(.*?)\*\*/g, '<strong class="text-white">$1</strong>') 
                  }} />
                </div>
              </div>
            </div>
          </div>

          {/* Conclusion - Pleine largeur en bas */}
          <div 
            className={`relative transition-all duration-700 ${
              sectionInView 
                ? 'opacity-100 translate-y-0 scale-100' 
                : 'opacity-0 translate-y-20 scale-95'
            }`}
            style={{ transitionDelay: '800ms' }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-[#DC1A5F]/20 via-purple-500/20 to-[#32D6E0]/20 rounded-3xl blur-xl"></div>
            <div className="relative bg-gradient-to-br from-gray-900 to-black p-8 md:p-12 rounded-3xl border-2 border-transparent shadow-2xl" style={{ 
              background: 'linear-gradient(135deg, #1f2937, #111827) padding-box, linear-gradient(90deg, #DC1A5F 0%, #32D6E0 100%) border-box'
            }}>
              <div className="text-center max-w-4xl mx-auto">
                <p className="text-xl md:text-2xl text-gray-100 leading-relaxed" dangerouslySetInnerHTML={{ 
                  __html: getTranslation(language, 'about.conclusion.part1').replace(/\*\*(.*?)\*\*/g, '<strong class="text-white">$1</strong>') 
                }} />
                <div className="mt-6 pt-6 border-t-2 border-gray-700">
                  <p className="text-lg md:text-xl text-gray-200">
                    {getTranslation(language, 'about.conclusion.part2')}<br className="hidden md:block" />
                    <span className="text-2xl md:text-3xl font-bold">
                      <span className="text-[#32D6E0]">{getTranslation(language, 'about.conclusion.functionality')}</span> & <span className="text-[#DC1A5F]">{getTranslation(language, 'about.conclusion.aesthetic')}</span>
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
